import type {
    CodeBlock,
    SlateBlock,
    SlateLeaf,
    BulletedListBlock,
    NumberedListBlock,
    ListItemBlock,
} from '@atlas/renderer';

export default function parseMarkdown(content: string): SlateBlock[] {
    const lines = content.replace(/\n{3,}/g, '\n\n').split('\n');

    const blocks: SlateBlock[] = [];

    let ongoing = false;

    for (let i = 0; i < lines.length; i++) {
        const codeMatch = extractCodeBlock(lines.slice(i));
        if (codeMatch) {
            blocks.push(codeMatch[0]);
            i += codeMatch[1] - 1;
            continue;
        }

        const listMatch = extractListBlock(lines.slice(i));
        if (listMatch) {
            blocks.push(listMatch[0]);
            i += listMatch[1] - 1;
            continue;
        }

        const line = lines[i];

        const lastBlock = blocks[blocks.length - 1];

        if (line.length === 0) {
            ongoing = false;
            continue;
        }

        if (line.startsWith('# ')) {
            blocks.push({
                type: 'heading-one',
                children: parseMarkdownLine(line.slice(2).trim()),
            });

            ongoing = true;
            continue;
        }

        if (line.match(/^#{2}\s/)) {
            blocks.push({
                type: 'heading-two',
                children: parseMarkdownLine(line.replace(/^#+/, '').trim()),
            });

            ongoing = true;
            continue;
        }

        if (line.match(/^#{3,6}\s/)) {
            blocks.push({
                type: 'heading-three',
                children: parseMarkdownLine(line.replace(/^#+/, '').trim()),
            });

            ongoing = true;
            continue;
        }

        if (line.match(/^-+$/)) {
            blocks.push({ type: 'horizontal-line', children: [{ text: '' }] });
            ongoing = false;
            continue;
        }

        if (ongoing && lastBlock?.type === 'paragraph') {
            const parsedLine = parseMarkdownLine(line);
            if (lastBlock.children.length && parsedLine.length) {
                parsedLine[0].text = '\n' + parsedLine[0].text;
            }
            lastBlock.children = lastBlock.children.concat(parsedLine);
        } else {
            blocks.push({ type: 'paragraph', children: parseMarkdownLine(line) });
            ongoing = true;
        }
    }

    return blocks;
}

// #region Helpers

function parseMarkdownLine(line: string): SlateLeaf[] {
    let rest = line;
    let match: RegExpMatchArray | null;
    const nodes: SlateLeaf[] = [];
    while (
        (match = rest.match(
            /((\*\*|`)(?:(?=(\\?))\3.)*?\2|\[([^[]+)\]\(([a-zA-Z]+:\/\/[-a-zA-Z0-9()@:%_+.~#?&//=]+)\))/,
        ))
    ) {
        if (match.index) nodes.push({ text: rest.slice(0, match.index) });
        const command = match[0][0] === match[0][1] ? match[0].slice(0, 2) : match[0][0];
        if (command === '[' && match[4] && match[5]) {
            const patch = { link: match[5] };
            nodes.push(...parseMarkdownLine(match[4]).map((node) => Object.assign(node, patch)));
        } else {
            const patch = command === '**' ? { bold: true } : command === '`' ? { code: true } : null;
            nodes.push(
                ...parseMarkdownLine(match[0].slice(command.length, 0 - command.length)).map((node) =>
                    Object.assign(node, patch),
                ),
            );
        }
        rest = rest.slice((match.index || 0) + match[0].length);
    }

    if (rest.length) nodes.push({ text: rest });

    return nodes;
}

function extractCodeBlock(lines: string[]): [block: CodeBlock, usedLines: number] | null {
    const firstLine = lines[0];
    if (!firstLine) return null;

    const match = firstLine.match(/^(\s*)```(.*)$/);
    if (!match) return null;

    const firstLineOffset = match[1].length;

    const index = lines.slice(1).findIndex((line) => new RegExp(`^\\s{0,${firstLineOffset}}\`\`\`\\s*$`).test(line));

    const length = ~index ? index + 1 : lines.length;

    const affectedLines = lines.slice(1, length);
    const normalized = firstLineOffset
        ? affectedLines.map((line) => line.replace(new RegExp(`^\\s{0,${firstLineOffset}}`), ''))
        : affectedLines;
    return [{ type: 'code-block', children: [{ text: match[2] + normalized.join('\n') }] }, length + 1];
}

function extractListItemBlock(lines: string[]): [block: ListItemBlock, usedLines: number] | null {
    const firstLine = lines[0];
    if (!firstLine) return null;

    const match = firstLine.match(/^(\s*)[-—*]\s(.*)$/);
    if (!match) return null;

    const firstLineOffset = match[1].length;

    const index = lines
        .slice(1)
        .findIndex((line) => !new RegExp(`^\\s{${firstLineOffset + 1},}`).test(line) || /^\s*[-—*](\s|$)/.test(line));

    const length = ~index ? index + 1 : lines.length;

    const affectedLines = lines.slice(1, length);
    const normalized = firstLineOffset
        ? affectedLines.map((line) =>
              line.replace(new RegExp(`^\\s{${firstLineOffset + 1},${firstLineOffset + 2}}`), ''),
          )
        : affectedLines;

    const content = (match[2] ?? '') + (affectedLines.length ? '\n' + normalized.join('\n') : '');

    return [{ type: 'list-item', children: parseMarkdownLine(content) }, length];
}

function extractListBlock(lines: string[]): [block: BulletedListBlock | NumberedListBlock, usedLines: number] | null {
    const matchListItem = extractListItemBlock(lines);
    if (!matchListItem) return null;

    const firstLineOffset = lines[0].match(/^(\s*)[-—*]\s(.*)$/)?.[1].length ?? 0;

    const children: BulletedListBlock['children'] = [matchListItem[0]];
    let index = matchListItem[1];
    let nextList;

    while (
        new RegExp(`^\\s{${firstLineOffset}}`).test(lines[index]) &&
        (nextList = extractListBlock(lines.slice(index)))
    ) {
        if (new RegExp(`^\\s{${firstLineOffset}}[^\\s]`).test(lines[index])) {
            children.push(...nextList[0].children);
        } else {
            children.push(nextList[0]);
        }
        index += nextList[1];
    }

    return [{ type: 'bulleted-list', children }, index];
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('parseMarkdown', async () => {
        it('parses markdown content', () => {
            // TODO: Check mix of inline formats in one paragraph
            // TODO: Check mix of inline formats on one string
            // TODO: Check different spacing between blocks
            // TODO: Check numbered-list
            // TODO: Check images

            const content = `# Heading 1\n## Heading 2\n### Heading 3\n\n---\nParagraph with **bold** text.\n\n\`\`\`\nconsole.log('Hello, world!');\n\`\`\`\n\n- List item 1\n- List item 2\n    - Nested list item 1\n    - Nested list item 2\n[Link](https://atlas.com)`;

            const blocks = parseMarkdown(content);

            expect(blocks).toEqual([
                { type: 'heading-one', children: [{ text: 'Heading 1' }] },
                { type: 'heading-two', children: [{ text: 'Heading 2' }] },
                { type: 'heading-three', children: [{ text: 'Heading 3' }] },
                { type: 'horizontal-line', children: [{ text: '' }] },
                {
                    type: 'paragraph',
                    children: [{ text: 'Paragraph with ' }, { text: 'bold', bold: true }, { text: ' text.' }],
                },
                { type: 'code-block', children: [{ text: "console.log('Hello, world!');" }] },
                {
                    type: 'bulleted-list',
                    children: [
                        {
                            type: 'list-item',
                            children: [{ text: 'List item 1' }],
                        },
                        {
                            type: 'list-item',
                            children: [{ text: 'List item 2' }],
                        },
                        {
                            type: 'bulleted-list',
                            children: [
                                {
                                    type: 'list-item',
                                    children: [{ text: 'Nested list item 1' }],
                                },
                                {
                                    type: 'list-item',
                                    children: [{ text: 'Nested list item 2' }],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    children: [{ text: 'Link', link: 'https://atlas.com' }],
                },
            ]);
        });
    });
}

// #endregion
